import { useId, useState, type InputHTMLAttributes } from 'react';

import { cx } from '../../lib/cx';
import { Icon } from '../Icon';

import styles from './TextField.module.css';

export interface TextFieldProps extends InputHTMLAttributes<HTMLInputElement> {
  /** Shown above the field; it is the field's name. */
  label: string;
  /** A line under the field: what to type, or what is wrong with it. */
  hint?: string;
  /** What is typed is wrong: a red border, and `aria-invalid` for assistive technology. */
  invalid?: boolean;
  /** The label is read by assistive technology but not shown: where the layout names the field. */
  hideLabel?: boolean;
  /** For a password: what the button that shows it says, and what the one that hides it again says. */
  showPasswordLabel?: string;
  hidePasswordLabel?: string;
}

/**
 * One line of text with its label. 44, 48 or 52 px high, by device.
 *
 * A password field has a button at its end that shows what was typed, and hides it
 * again: a long password is easy to mistype where nobody can see it.
 */
export function TextField({
  label,
  hint,
  className,
  id: given,
  type,
  invalid = false,
  hideLabel = false,
  showPasswordLabel = 'Show password',
  hidePasswordLabel = 'Hide password',
  ...rest
}: TextFieldProps) {
  const generated = useId();
  const id = given ?? generated;
  const [shown, setShown] = useState(false);
  const password = type === 'password';
  const input = (
    <input
      id={id}
      aria-describedby={hint ? `${id}-hint` : undefined}
      aria-invalid={invalid || undefined}
      className={cx(styles.input, password && styles.withButton, invalid && styles.invalid)}
      type={password && shown ? 'text' : type}
      {...rest}
    />
  );
  return (
    <div className={cx(styles.field, className)}>
      <label htmlFor={id} className={cx(styles.label, hideLabel && styles.hiddenLabel)}>
        {label}
      </label>
      {password ? (
        <div className={styles.control}>
          {input}
          <button
            type="button"
            className={styles.reveal}
            aria-label={shown ? hidePasswordLabel : showPasswordLabel}
            title={shown ? hidePasswordLabel : showPasswordLabel}
            aria-pressed={shown}
            aria-controls={id}
            disabled={rest.disabled}
            onClick={() => setShown(!shown)}
          >
            <Icon name={shown ? 'eye-off' : 'eye'} size={20} />
          </button>
        </div>
      ) : (
        input
      )}
      {hint ? (
        <p id={`${id}-hint`} className={styles.hint}>
          {hint}
        </p>
      ) : null}
    </div>
  );
}
